import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import cls from 'classnames';

import { useMount } from '@cutwise/utils';

import { urlAPI } from '@/api/urlAPI';
import PersonIcon from '@/assets/icons/icon-person.svg?react';
import ContactsPopup from '@/components/ContactsPopup/ContactsPopup';
import Disclaimer from '@/components/Disclaimer/Disclaimer';
import ErrorPage from '@/components/ErrorPage/ErrorPage';
import Header from '@/components/Header/Header';
import { DEFAULT_COLLECTION_ID } from '@/constants/routes';
import { Diamond } from '@/entities/Diamond';
import DiamondRow from '@/routes/DiamondsListPage/DiamondRow/DiamondRow';
import { useStore } from '@/stores/StoreProvider';
import Button from '@/ui-kit/Button/Button';
import CirclesLoader from '@/ui-kit/CirclesLoader/CirclesLoader';
import { isURL } from '@/utils/isURL';

import styles from './DiamondsListPage.module.scss';

function DiamondsListPage() {
  const { fetchDiamondList, collection, contacts, reports, listError, openContactsPopup } = useStore('diamondsStore');
  const { collectionId = DEFAULT_COLLECTION_ID } = useParams();

  useMount(() => fetchDiamondList(collectionId));

  if (listError) {
    return <ErrorPage error={listError} />;
  }

  if (!collection) {
    return <CirclesLoader />;
  }

  function parseDescriptionText(): string {
    if (!collection!.ownerText) {
      return '';
    }

    return collection!.ownerText
      .replaceAll('\n', ' \n ')
      .split(' ')
      .map((word) => word.trim())
      .map((word) => (isURL(word) ? `<a target="_blank" href="${word}">${word}</a>` : word))
      .join(' ');
  }

  return (
    <Fragment>
      <Helmet>
        <title>{collection.headerText}</title>

        <meta name="theme-color" content="#1c1c1c" />
      </Helmet>

      <div className={styles.root}>
        <Header href={urlAPI.new(collectionId)} className={styles.header} />

        <div>
          <div className={styles.heading}>
            <div className="container">
              <h2 className={styles.subtitle}>{collection.headerText}</h2>

              {collection.ownerText && <p className={styles.description} dangerouslySetInnerHTML={{ __html: parseDescriptionText() }} />}

              {contacts && (
                <div className={styles.seller}>
                  <Button onClick={openContactsPopup} startIcon={<PersonIcon />}>
                    <span>Contact</span>

                    {contacts.title && <span> {contacts.title}</span>}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div>
            {reports.map((diamond: Diamond, i: number) => (
              <div key={diamond.id ?? i} className={cls('container', styles.diamond)}>
                {diamond.isValid && (
                  <Link to={urlAPI.new(`/product/${diamond.id}/${collectionId}`)} className={styles.link}>
                    <DiamondRow diamond={diamond} />
                  </Link>
                )}

                {!diamond.isValid && <DiamondRow diamond={diamond} />}
              </div>
            ))}
          </div>

          <div className="container">
            <Disclaimer plural />
          </div>
        </div>
      </div>

      {contacts && <ContactsPopup contactsData={contacts} />}
    </Fragment>
  );
}

export default observer(DiamondsListPage);
